import {
  Component,
  OnInit,
  EventEmitter,
  Input,
  Output,
  ViewContainerRef,
  OnChanges,
  SimpleChanges,
  OnDestroy,
} from '@angular/core';
import {
  UntypedFormBuilder,
  UntypedFormArray,
  UntypedFormControl,
  UntypedFormGroup,
  Validators,
} from '@angular/forms';
import { FormComponentData } from '../../../../core/types/form-component-data';
import { ClientService } from '../../../../core/services/client.service';
import { TranslateService } from '@ngx-translate/core';
import { DialogProviderService } from '@app/core/services/dialog-provider.service';
import { IdentifierValidation } from '../../../../core/validation/identifier-validation';
import { BankAccount } from '@app/core/types/bank-account';
import { Subject, takeUntil } from 'rxjs';
import { cloneDeep, isEqual } from 'lodash-es';
import { NotificationService } from '../../../../core/services/notification.service';
import { Client } from '@app/core/types/client';
import { ClientDocument } from '@app/core/types/client-document';

@Component({
  selector: 'itfg-bank-accounts-data',
  templateUrl: './bank-accounts-data.component.html',
  styleUrls: ['./bank-accounts-data.component.scss'],
  standalone: false
})
export class BankAccountsDataComponent implements OnInit, OnChanges, OnDestroy {
  @Input() data: any;
  @Input() client: Client;
  @Input() isLoading: boolean;

  @Output() dataChanged = new EventEmitter<FormComponentData>();
  @Output() documentDeleted = new EventEmitter<any>();

  bankAccountForms: UntypedFormArray = new UntypedFormArray([]);
  saveBtnEnabled: boolean[] = [];
  indexOfEditedAccount: number | null = null;

  MAX_FILE_NAME_LENGTH = 64;

  private _unsubscribe = new Subject<void>();

  constructor(
    private formBuilder: UntypedFormBuilder,
    private clientService: ClientService,
    private translateService: TranslateService,
    private dialogService: DialogProviderService,
    private notificationService: NotificationService,
    private viewContainerRef: ViewContainerRef,
  ) { }

  ngOnInit() {
    this.setInitialData();

    this.clientService.onBankAccoutDataChange
      .pipe(takeUntil(this._unsubscribe))
      .subscribe(data => {
        this.data = data;
        this.setInitialData();
      });
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes.data && changes.data.currentValue) {
      this.setInitialData();
    }
  }

  setInitialData() {
    const bankAccounts = this.normalizeBankAccounts();

    if (bankAccounts.length === 0) {
      this.bankAccountForms = this.formBuilder.array([]);
      this.saveBtnEnabled = [];
    } else {
      this.mapBankAccountsToForm(bankAccounts);
    }
  }

  normalizeBankAccounts(): BankAccount[] {
    if (!this.data) return [];
    if (Array.isArray(this.data)) return this.data;
    if (this.data.bankAccounts && Array.isArray(this.data.bankAccounts)) {
      return this.data.bankAccounts;
    }
    return [];
  }

  mapBankAccountsToForm(bankAccounts: BankAccount[]) {
    this.bankAccountForms = this.formBuilder.array(
      bankAccounts.map((account, formIndex): UntypedFormGroup => {
        const formGroup = this.createExistingBankAccountFormGroup(account);
        this.saveBtnEnabled[formIndex] = false;
        this.watchIndividualFormChanges(formGroup, formIndex);
        return formGroup;
      })
    );
  }

  createExistingBankAccountFormGroup(bankAccount: BankAccount): UntypedFormGroup {
    return new UntypedFormGroup({
      id: new UntypedFormControl(bankAccount.id),
      iban: new UntypedFormControl(
        { value: bankAccount.iban, disabled: true },
        [IdentifierValidation.isValidIban, Validators.required]
      ),
      userDocument: new UntypedFormControl(
        bankAccount.userDocument?.name || bankAccount.userDocument || ''
      ),
      userDocumentObject: new UntypedFormControl(bankAccount.userDocument),
      verified: new UntypedFormControl({
        value: bankAccount.verified,
        disabled: true,
      }),
    });
  }

  createNewBankAccountFormGroup(): UntypedFormGroup {
    return new UntypedFormGroup({
      id: new UntypedFormControl(null),
      iban: new UntypedFormControl('', [
        IdentifierValidation.isValidIban,
        Validators.required,
      ]),
      userDocument: new UntypedFormControl(''),
      userDocumentObject: new UntypedFormControl(null),
      verified: new UntypedFormControl(false),
    });
  }

  watchIndividualFormChanges(formGroup: UntypedFormGroup, formIndex: number) {
    const initialFormValue = cloneDeep(formGroup.getRawValue());

    const subscription = formGroup.valueChanges
      .pipe(takeUntil(this._unsubscribe))
      .subscribe(() => {
        const isExistingAccount = formGroup.get('id').value !== null;
        const currentValue = formGroup.getRawValue();
        const hasFormValueChanged = !isEqual(currentValue, initialFormValue);

        this.saveBtnEnabled[formIndex] =
          (isExistingAccount && hasFormValueChanged && formGroup.valid) ||
          (!isExistingAccount && formGroup.valid);

        this.emitFormState();
      });

    formGroup['_valueChangesSubscription'] = subscription;
  }

  watchIndividualFormChangesWithInitialValue(
    formGroup: UntypedFormGroup,
    formIndex: number,
    customInitialValue: any
  ) {
    if (formGroup['_valueChangesSubscription']) {
      formGroup['_valueChangesSubscription'].unsubscribe();
    }

    const subscription = formGroup.valueChanges
      .pipe(takeUntil(this._unsubscribe))
      .subscribe(() => {
        const isExistingAccount = formGroup.get('id').value !== null;
        const currentValue = formGroup.getRawValue();
        const hasFormValueChanged = !isEqual(currentValue, customInitialValue);

        this.saveBtnEnabled[formIndex] =
          (isExistingAccount && hasFormValueChanged && formGroup.valid) ||
          (!isExistingAccount && formGroup.valid);

        this.emitFormState();
      });

    formGroup['_valueChangesSubscription'] = subscription;
  }

  emitFormState() {
    const hasAnyFormChanged = this.saveBtnEnabled.some((enabled: boolean) => enabled);
    const allFormsValid = this.bankAccountForms.controls.every(
      (form: UntypedFormGroup) => form.valid
    );

    this.dataChanged.emit({
      valid: allFormsValid,
      data: this.bankAccountForms.value,
      dirty: hasAnyFormChanged,
    });
  }

  addBankRecord() {
    const newBankAccount = this.createNewBankAccountFormGroup();
    this.watchIndividualFormChanges(newBankAccount, this.bankAccountForms.length);

    this.bankAccountForms.push(newBankAccount);
    this.saveBtnEnabled.push(false);

    this.indexOfEditedAccount = this.bankAccountForms.length - 1;
  }

  editBankAccountRecord(index: number, currBankAccount: UntypedFormGroup) {
    const initialFormValue = cloneDeep(currBankAccount.getRawValue());

    currBankAccount.get('iban').enable();
    currBankAccount.get('verified').enable();

    this.indexOfEditedAccount = index;

    this.watchIndividualFormChangesWithInitialValue(currBankAccount, index, initialFormValue);
  }

  saveEditedBankAccountRecord(bankAccount: UntypedFormGroup) {
    const formIndex = this.bankAccountForms.controls.indexOf(bankAccount);
    this.onSaveBankAccount(formIndex);
  }

  saveAllChangedBankAccounts() {
    this.bankAccountForms.controls.forEach((formGroup: UntypedFormGroup, index: number) => {
      if (this.saveBtnEnabled[index]) {
        this.onSaveBankAccount(index);
      }
    });
  }

  onSaveBankAccount(formIndex: number) {
    const form = this.bankAccountForms.at(formIndex) as UntypedFormGroup;

    if (!form.valid) {
      this.notificationService.showLocalizedErrorMessage({
        notificationText: 'forms.formSavedIsInvalid',
      });
      return;
    }

    const bankAccountData = form.getRawValue();
    const bankAccountId = bankAccountData.id;
    const userDocValue = bankAccountData.userDocument;
    const file = userDocValue instanceof File ? userDocValue : null;

    const formData = new FormData();
    if (file) {
      formData.append('file', file);
    }

    if (bankAccountId) {
      this.clientService
        .updateClientBankAccount(
          this.client.id,
          bankAccountId,
          {
            iban: bankAccountData.iban,
            verified: bankAccountData.verified,
            id: bankAccountId
          },
          formData
        )
        .pipe(takeUntil(this._unsubscribe))
        .subscribe({
          next: (updatedAccount) => {
            if (updatedAccount.userDocument) {
              this.documentDeleted.emit({
                action: 'added',
                document: updatedAccount.userDocument
              });
            }

            this.handleSaveSuccess(formIndex, updatedAccount, 'edited');
          },
          error: (err) => {
            this.handleSaveError(err, 'edit');
          }
        });
    } else {
      this.clientService
        .saveClientBankAccount(
          this.client.id,
          {
            iban: bankAccountData.iban,
            verified: bankAccountData.verified
          },
          formData
        )
        .pipe(takeUntil(this._unsubscribe))
        .subscribe({
          next: (newAccount) => {
            if (newAccount.userDocument) {
              this.documentDeleted.emit({
                action: 'added',
                document: newAccount.userDocument
              });
            }

            this.handleSaveSuccess(formIndex, newAccount, 'created');
          },
          error: (err) => {
            this.handleSaveError(err, 'create');
          }
        });
    }
  }

  handleSaveSuccess(formIndex: number, savedAccount: BankAccount, action: 'created' | 'edited') {
    const oldForm = this.bankAccountForms.at(formIndex) as UntypedFormGroup;
    if (oldForm['_valueChangesSubscription']) {
      oldForm['_valueChangesSubscription'].unsubscribe();
    }

    const newForm = this.createExistingBankAccountFormGroup(savedAccount);
    this.bankAccountForms.setControl(formIndex, newForm);

    this.watchIndividualFormChanges(newForm, formIndex);

    this.saveBtnEnabled[formIndex] = false;
    this.indexOfEditedAccount = null

    this.updateBankAccountsList(savedAccount);

    this.translateService
      .get([`clients.${action}Account`], { iban: savedAccount.iban })
      .pipe(takeUntil(this._unsubscribe))
      .subscribe((translation) => {
        this.notificationService.showLocalizedSuccessMessage({
          notificationText: translation[`clients.${action}Account`],
        });
      });
  }

  handleSaveError(error: any, action: 'create' | 'edit') {
    console.error(`Error ${action}ing bank account:`, error);
    if (error.error[0]?.message) {
      this.notificationService.showLocalizedErrorMessage({
        notificationText: error.error[0].message,
      });
    } else {
      this.notificationService.showLocalizedErrorMessage({
        notificationText: `clients.${action}BankAccountError`,
      });
    }
  }

  updateBankAccountsList(updatedAccount: BankAccount) {
    let bankAccounts = this.normalizeBankAccounts();

    const accountIndex = bankAccounts.findIndex(
      acc => acc.id === updatedAccount.id
    );

    if (accountIndex !== -1) {
      bankAccounts[accountIndex] = updatedAccount;
    } else {
      bankAccounts = [updatedAccount, ...bankAccounts];
    }

    this.updateSingleFormWithoutReset(updatedAccount);
  }

  private updateSingleFormWithoutReset(updatedAccount: BankAccount) {
    const formIndex = this.bankAccountForms.controls.findIndex(
      (form: UntypedFormGroup) => {
        const formValue = form.getRawValue();
        return formValue.iban === updatedAccount.iban ||
          (!formValue.id && formValue.iban === updatedAccount.iban);
      }
    );

    if (formIndex !== -1) {
      const newForm = this.createExistingBankAccountFormGroup(updatedAccount);
      this.bankAccountForms.setControl(formIndex, newForm);
      this.watchIndividualFormChanges(newForm, formIndex);
      this.saveBtnEnabled[formIndex] = false;
    }
  }

  deleteBankRecord(index: number, bankAccount: BankAccount) {
    const form = this.bankAccountForms.at(index) as UntypedFormGroup;
    const bankAccountData = form.getRawValue();
    const bankAccountId = bankAccountData.id;
    const userDocumentObject = bankAccountData.userDocumentObject;

    this.translateService
      .get(
        [
          'clients.deleteBankRecordMessage',
          'global.delete',
          'global.cancel',
          'global.confirm',
        ],
        { iban: bankAccountData.iban }
      )
      .pipe(takeUntil(this._unsubscribe))
      .subscribe(translation => {
        const dialogConfig = {
          message: translation['clients.deleteBankRecordMessage'],
          disableClose: false,
          viewContainerRef: this.viewContainerRef,
          title: translation['global.delete'],
          cancelButton: translation['global.cancel'],
          acceptButton: translation['global.confirm'],
        };

        if (bankAccountId) {
          this.dialogService
            .openConfirm(dialogConfig)
            .afterClosed()
            .pipe(takeUntil(this._unsubscribe))
            .subscribe((accept: boolean) => {
              if (accept) {
                this.indexOfEditedAccount = null;

                if (userDocumentObject && userDocumentObject.id) {
                  this.deleteDocumentOfBankAccount(
                    userDocumentObject,
                    bankAccountId,
                    index
                  );
                } else {
                  this.deleteBankAccount(bankAccountId, index);
                }
              }
            });
        } else {
          if (form['_valueChangesSubscription']) {
            form['_valueChangesSubscription'].unsubscribe();
          }

          this.bankAccountForms.removeAt(index);
          this.saveBtnEnabled.splice(index, 1);


        }
      });
  }

  deleteDocumentOfBankAccount(
    document: ClientDocument,
    bankAccountId: number,
    formIndex: number
  ) {
    this.clientService
      .deleteClientDocument(this.client.id, document.id)
      .pipe(takeUntil(this._unsubscribe))
      .subscribe({
        next: () => {
          this.documentDeleted.emit({
            action: 'deleted',
            document: document
          });

          this.deleteBankAccount(bankAccountId, formIndex);
        },
        error: (err) => {
          console.error('Error deleting document:', err);
          this.notificationService.showLocalizedErrorMessage({
            notificationText: 'clients.deleteDocumentError',
          });
        }
      });
  }

  deleteBankAccount(bankAccountId: number, formIndex: number) {
    this.clientService
      .deleteClientBankAccount(this.client.id, bankAccountId)
      .pipe(takeUntil(this._unsubscribe))
      .subscribe({
        next: () => {
          const form = this.bankAccountForms.at(formIndex) as UntypedFormGroup;
          if (form['_valueChangesSubscription']) {
            form['_valueChangesSubscription'].unsubscribe();
          }

          this.bankAccountForms.removeAt(formIndex);
          this.saveBtnEnabled.splice(formIndex, 1);

          this.notificationService.showLocalizedSuccessMessage({
            notificationText: this.translateService.instant('clients.deletedAccount', { iban: form.get('iban').value })
          });

          this.refreshBankAccountsList();
        },
        error: (err) => {
          console.error('Error deleting bank account:', err);
          this.notificationService.showLocalizedErrorMessage({
            notificationText: 'clients.deleteBankAccountError',
          });
        }
      });
  }

  refreshBankAccountsList() {
    this.clientService
      .getClientBankAccounts(this.client.id)
      .pipe(takeUntil(this._unsubscribe))
      .subscribe({
        next: (bankAccounts) => {
          this.clientService.onBankAccoutDataChange.next({ bankAccounts });
        },
        error: (err) => {
          console.error('Error refreshing bank accounts:', err);
        }
      });
  }

  onFileSelected(file: File, control: UntypedFormControl) {
    control.patchValue(file);
  }

  clearFileInput(index: number, event: any) {
    event.preventDefault();
    event.stopPropagation();

    const form = this.bankAccountForms.at(index) as UntypedFormGroup;
    const hasExistingDocument = form.get('userDocumentObject').value;

    if (hasExistingDocument && hasExistingDocument.id) {
      this.onClearDocument(index);
    } else {
      form.get('userDocument').setValue(null);
    }
  }

  onClearDocument(formIndex: number) {
    const form = this.bankAccountForms.at(formIndex) as UntypedFormGroup;
    const userDocumentObject = form.get('userDocumentObject').value;
    const userDocumentName = form.get('userDocument').value;
    const iban = form.get('iban').value;

    this.translateService
      .get([
        'global.delete',
        'global.cancel',
        'global.confirm',
      ])
      .pipe(takeUntil(this._unsubscribe))
      .subscribe(translation => {
        const dialogConfig = {
          message: this.translateService.instant('clients.deleteDocumentMessage', { documentName: userDocumentName }),
          disableClose: false,
          viewContainerRef: this.viewContainerRef,
          title: translation['global.delete'],
          cancelButton: translation['global.cancel'],
          acceptButton: translation['global.confirm'],
        };

        this.dialogService
          .openConfirm(dialogConfig)
          .afterClosed()
          .pipe(takeUntil(this._unsubscribe))
          .subscribe((accept: boolean) => {
            if (accept) {
              this.clientService
                .deleteClientDocument(this.client.id, userDocumentObject.id)
                .pipe(takeUntil(this._unsubscribe))
                .subscribe({
                  next: () => {
                    this.documentDeleted.emit({
                      action: 'deleted',
                      document: userDocumentObject
                    });

                    form.get('userDocument').setValue('');
                    form.get('userDocumentObject').setValue(null);

                    this.refreshBankAccountsList();

                    this.notificationService.showLocalizedSuccessMessage({
                      notificationText: this.translateService.instant('clients.deletedDocument', { documentName: userDocumentName, iban: iban }),
                    });
                  },
                  error: (err) => {
                    console.error('Error deleting document:', err);
                    this.notificationService.showLocalizedErrorMessage({
                      notificationText: 'clients.deleteDocumentError',
                    });
                  }
                });
            }
          });
      });
  }

  trimEmptyChars(index: number, iban: UntypedFormControl, event: any) {
    event.preventDefault();
    const clipBoardData = event.clipboardData.getData('Text');
    iban.setValue(clipBoardData.replace(/ /g, ''));
  }

  ngOnDestroy() {
    this._unsubscribe.next();
    this._unsubscribe.complete();
  }
}